import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useAuthContext } from './contexts.tsx';
import { resolveApiBaseUrl } from './apiFetch.ts';
import { applyLiveEvent } from './reconciliation.ts';

/**
 * SignalR connection states.
 *
 * `FailedPermanently` is a TERMINAL state: the client has exhausted its
 * reconnect budget and stopped retrying. Unlike `Disconnected` (a transient
 * state the hub keeps trying to recover from), the UI should surface a
 * "realtime disconnected — reload" affordance rather than waiting for an
 * automatic recovery that will never come.
 */
export type ConnectionState =
  | 'Connecting'
  | 'Connected'
  | 'Reconnecting'
  | 'Disconnected'
  | 'FailedPermanently';

/**
 * SignalR connection object interface
 */
export interface SignalRConnection {
  start: () => Promise<void>;
  stop: () => Promise<void>;
  invoke: (methodName: string, ...args: unknown[]) => Promise<unknown>;
  on: (methodName: string, callback: (...args: unknown[]) => void) => void;
  off: (methodName: string, callback?: (...args: unknown[]) => void) => void;
}

/**
 * Result from useSignalR hook
 */
export interface UseSignalRResult {
  connection: SignalRConnection | null;
  connectionState: ConnectionState;
  error: Error | null;
}

/**
 * Map from hook-level hub names to the backend route segments registered in
 * Program.cs (e.g. MapHub<AlarmHub>("/hubs/alarms")).
 *
 * Accepts both PascalCase class-style names ("AlarmHub") and lowercase names
 * ("alarms", "equipment") so callers don't need to know the backend path.
 */
const HUB_NAME_MAP: Record<string, string> = {
  // PascalCase (used in useRealtime.ts)
  AlarmHub: 'alarms',
  EquipmentHub: 'equipment',
  ProductionHub: 'production',
  NotificationHub: 'notifications',
  // lowercase (used in production-ui hooks & the shell invalidation bridge)
  alarms: 'alarms',
  equipment: 'equipment',
  production: 'production',
  notifications: 'notifications',
  workOrders: 'work-orders',
  batch: 'batch',
  BatchHub: 'batch',
  // Edge-fleet liveness push (FleetHub at /hubs/edgefleet) — NodeUpdated +
  // DeploymentUpdated drive near-instant console refresh on top of polling.
  edgefleet: 'edgefleet',
  FleetHub: 'edgefleet',
  // Multi-Site liveness push (SiteHub at /hubs/multisite) — SiteStatusUpdated +
  // ConflictUpdated drive near-instant console refresh on top of polling.
  multisite: 'multisite',
  SiteHub: 'multisite',
};

const resolveHubPath = (hubName: string): string =>
  HUB_NAME_MAP[hubName] ?? hubName;

const buildHubUrl = (hubPath: string): string => {
  const baseUrl = resolveApiBaseUrl();
  return `${baseUrl.replace(/\/api\/v\d+$/, '')}/hubs/${hubPath}`;
};

// ─── Shared connection registry ────────────────────────────────────────────
//
// One HubConnection per hub *path* per client, ref-counted. Before this, every
// `useSignalR` / `useSignalREvent` / `useSignalRGroup` / `useHubConnectionState`
// call opened its own WebSocket — a single SCADA tile could open three sockets
// to the same hub, and a 20-tile wall opened dozens. The provider multiplexes:
// the Nth subscriber to a hub reuses the first subscriber's socket.

interface HubEntry {
  /** The underlying @microsoft/signalr HubConnection (typed structurally). */
  connection: SignalRConnection | null;
  /** Number of live consumers (hooks) holding this hub. */
  refCount: number;
  /** Current lifecycle state, fanned out to every subscriber. */
  state: ConnectionState;
  /** Last connection error, if any. */
  error: Error | null;
  /** Subscribers notified whenever state/connection/error change. */
  subscribers: Set<() => void>;
  /** In-flight connect promise so concurrent acquires don't double-start. */
  starting: Promise<void> | null;
  /** Manual reconnect timer handle (used after automatic-reconnect exhausts). */
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  /** Reconnect attempt counter for the manual backoff path. */
  reconnectAttempts: number;
  /** True once the entry has been released to zero and torn down. */
  disposed: boolean;
}

interface HubRegistry {
  acquire: (hubName: string) => void;
  release: (hubName: string) => void;
  subscribe: (hubName: string, cb: () => void) => () => void;
  snapshot: (hubName: string) => UseSignalRResult;
}

const HubRegistryContext = createContext<HubRegistry | null>(null);

// Manual reconnect backoff (used once @microsoft/signalr's automatic
// reconnect attempts are exhausted) — capped exponential with jitter.
const manualReconnectDelay = (attempt: number): number => {
  const base = 1000;
  const max = 30000;
  const delay = Math.min(base * Math.pow(2, attempt), max);
  return delay + Math.random() * 1000;
};

// Upper bound on manual reconnect attempts before giving up permanently. With
// the capped-exponential schedule above (1s → 30s, then 30s steady) ~20
// attempts spans roughly 5 minutes of trying; past that, a hub that still
// can't connect is almost certainly down for reasons a retry won't fix
// (revoked token, gateway offline, route removed), so we stop the spin loop
// and move the hub to the terminal `FailedPermanently` state. The UI can then
// prompt a reload instead of silently retrying forever.
const MAX_MANUAL_RECONNECT_ATTEMPTS = 20;

/**
 * Best-effort exhaustion metric. Emits to `window.__tyndoMetric` when a host
 * has installed one (the shell wires this to its telemetry pipeline); otherwise
 * a no-op. Kept dependency-free so the hooks package stays standalone.
 */
const emitExhaustionMetric = (hubPath: string): void => {
  if (typeof window === 'undefined') return;
  try {
    const sink = (window as unknown as {
      __tyndoMetric?: (name: string, attrs: Record<string, unknown>) => void;
    }).__tyndoMetric;
    if (typeof sink === 'function') {
      sink('realtime.connection_failed_permanently', { hub: hubPath });
    }
  } catch {
    /* metric sink must never break the connection lifecycle. */
  }
};

/**
 * Owns one ref-counted HubConnection per hub. Wrap the app (or each
 * micro-frontend shell) in this provider so all real-time hooks share sockets.
 *
 * Falls back gracefully if omitted: `useSignalR` and friends still work, each
 * opening its own connection (the legacy behavior), so existing trees that
 * haven't adopted the provider are not broken.
 */
export interface HubConnectionProviderProps {
  children: ReactNode;
  /**
   * Invoked once per hub when that hub exhausts its reconnect budget and enters
   * the terminal `FailedPermanently` state. Hosts can use this to surface a
   * "realtime disconnected — reload" affordance or forward a metric. Optional.
   */
  onExhausted?: (hubName: string) => void;
}

export const HubConnectionProvider: React.FC<HubConnectionProviderProps> = ({
  children,
  onExhausted,
}) => {
  const { token } = useAuthContext();
  const tokenRef = useRef<string | null>(token);
  tokenRef.current = token;
  const onExhaustedRef = useRef(onExhausted);
  onExhaustedRef.current = onExhausted;

  const entriesRef = useRef<Map<string, HubEntry>>(new Map());

  const notify = useCallback((entry: HubEntry) => {
    entry.subscribers.forEach((cb) => cb());
  }, []);

  const setState = useCallback(
    (entry: HubEntry, state: ConnectionState, error: Error | null = entry.error) => {
      entry.state = state;
      entry.error = error;
      notify(entry);
    },
    [notify],
  );

  const startConnection = useCallback(
    async (hubPath: string, entry: HubEntry): Promise<void> => {
      const currentToken = tokenRef.current;
      if (!currentToken) {
        setState(entry, 'Disconnected', new Error('No auth token available'));
        return;
      }
      setState(entry, entry.connection ? 'Reconnecting' : 'Connecting', null);
      try {
        const { HubConnectionBuilder } = await import('@microsoft/signalr');
        const conn = new HubConnectionBuilder()
          .withUrl(buildHubUrl(hubPath), {
            accessTokenFactory: () => tokenRef.current ?? '',
          })
          .withAutomaticReconnect([0, 2000, 5000, 10000, 20000])
          .withServerTimeout(30000)
          .build();

        conn.onreconnecting(() => {
          if (entry.disposed) return;
          setState(entry, 'Reconnecting');
        });
        conn.onreconnected(() => {
          if (entry.disposed) return;
          entry.reconnectAttempts = 0;
          setState(entry, 'Connected', null);
        });
        conn.onclose(() => {
          if (entry.disposed) return;
          setState(entry, 'Disconnected');
          // Automatic reconnect exhausted (or never engaged): keep retrying
          // with capped exponential backoff while consumers remain mounted.
          scheduleManualReconnect(hubPath, entry);
        });

        await conn.start();
        if (entry.disposed) {
          // Released while connecting — tear the fresh socket back down.
          void conn.stop().catch(() => undefined);
          return;
        }
        entry.connection = conn as unknown as SignalRConnection;
        entry.reconnectAttempts = 0;
        setState(entry, 'Connected', null);
      } catch (err) {
        if (entry.disposed) return;
        const error = err instanceof Error ? err : new Error(String(err));
        setState(entry, 'Disconnected', error);
        scheduleManualReconnect(hubPath, entry);
      } finally {
        entry.starting = null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setState],
  );

  const scheduleManualReconnect = useCallback(
    (hubPath: string, entry: HubEntry) => {
      if (entry.disposed || entry.refCount <= 0) return;
      // Budget exhausted: stop spinning and surface the terminal state so the
      // UI can prompt a reload. A later token change resets the budget (see the
      // token effect), giving an exhausted hub a path back after re-auth.
      if (entry.reconnectAttempts >= MAX_MANUAL_RECONNECT_ATTEMPTS) {
        setState(entry, 'FailedPermanently');
        emitExhaustionMetric(hubPath);
        onExhaustedRef.current?.(hubPath);
        return;
      }
      if (entry.reconnectTimer) clearTimeout(entry.reconnectTimer);
      const delay = manualReconnectDelay(entry.reconnectAttempts);
      entry.reconnectAttempts += 1;
      entry.reconnectTimer = setTimeout(() => {
        entry.reconnectTimer = null;
        if (entry.disposed || entry.refCount <= 0) return;
        if (!entry.starting) {
          entry.starting = startConnection(hubPath, entry);
        }
      }, delay);
    },
    [setState, startConnection],
  );

  const acquire = useCallback(
    (hubName: string) => {
      const hubPath = resolveHubPath(hubName);
      let entry = entriesRef.current.get(hubPath);
      if (!entry) {
        entry = {
          connection: null,
          refCount: 0,
          state: 'Disconnected',
          error: null,
          subscribers: new Set(),
          starting: null,
          reconnectTimer: null,
          reconnectAttempts: 0,
          disposed: false,
        };
        entriesRef.current.set(hubPath, entry);
      }
      entry.refCount += 1;
      entry.disposed = false;
      if (!entry.connection && !entry.starting) {
        entry.starting = startConnection(hubPath, entry);
      }
    },
    [startConnection],
  );

  const release = useCallback((hubName: string) => {
    const hubPath = resolveHubPath(hubName);
    const entry = entriesRef.current.get(hubPath);
    if (!entry) return;
    entry.refCount -= 1;
    if (entry.refCount <= 0) {
      entry.refCount = 0;
      entry.disposed = true;
      if (entry.reconnectTimer) {
        clearTimeout(entry.reconnectTimer);
        entry.reconnectTimer = null;
      }
      const conn = entry.connection;
      entry.connection = null;
      entry.state = 'Disconnected';
      if (conn) {
        void conn.stop().catch(() => undefined);
      }
      entriesRef.current.delete(hubPath);
    }
  }, []);

  const subscribe = useCallback((hubName: string, cb: () => void) => {
    const hubPath = resolveHubPath(hubName);
    const entry = entriesRef.current.get(hubPath);
    if (!entry) return () => undefined;
    entry.subscribers.add(cb);
    return () => {
      entry.subscribers.delete(cb);
    };
  }, []);

  const snapshot = useCallback((hubName: string): UseSignalRResult => {
    const hubPath = resolveHubPath(hubName);
    const entry = entriesRef.current.get(hubPath);
    if (!entry) {
      return { connection: null, connectionState: 'Disconnected', error: null };
    }
    return {
      connection: entry.connection,
      connectionState: entry.state,
      error: entry.error,
    };
  }, []);

  // When the token changes (login / refresh), restart every live hub so the
  // new bearer is used (the accessTokenFactory reads the ref, but an already
  // open socket keeps its original token until it reconnects).
  useEffect(() => {
    if (!token) return;
    entriesRef.current.forEach((entry, hubPath) => {
      if (entry.refCount <= 0) return;
      // A fresh token resets the reconnect budget: the most common reason a
      // hub failed permanently is an expired/revoked credential, so a new
      // bearer gets a full retry allowance and clears the terminal state.
      const wasExhausted = entry.state === 'FailedPermanently';
      if (wasExhausted) {
        entry.reconnectAttempts = 0;
        if (entry.reconnectTimer) {
          clearTimeout(entry.reconnectTimer);
          entry.reconnectTimer = null;
        }
      }
      if (entry.starting) return;
      if (entry.connection) {
        const old = entry.connection;
        entry.connection = null;
        void old.stop().catch(() => undefined);
        entry.starting = startConnection(hubPath, entry);
      } else if (wasExhausted) {
        // No live socket (it gave up) — kick off a fresh connect now that the
        // budget is restored.
        entry.starting = startConnection(hubPath, entry);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const registry = useMemo<HubRegistry>(
    () => ({ acquire, release, subscribe, snapshot }),
    [acquire, release, subscribe, snapshot],
  );
  return (
    <HubRegistryContext.Provider value={registry}>
      {children}
    </HubRegistryContext.Provider>
  );
};

// ─── Standalone (provider-less) fallback ─────────────────────────────────────
//
// When no HubConnectionProvider is mounted, fall back to a per-hook connection
// so existing trees keep working. This preserves the legacy contract.

const useStandaloneSignalR = (hubName: string): UseSignalRResult => {
  const { token } = useAuthContext();
  const [connection, setConnection] = useState<SignalRConnection | null>(null);
  const [connectionState, setConnectionState] =
    useState<ConnectionState>('Disconnected');
  const [error, setError] = useState<Error | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connRef = useRef<SignalRConnection | null>(null);
  const disposedRef = useRef(false);

  const getReconnectDelay = (attempt: number): number => {
    const baseDelay = 1000;
    const maxDelay = 30000;
    const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
    return delay + Math.random() * 1000;
  };

  const connect = useCallback(async () => {
    if (!token) {
      setError(new Error('No auth token available'));
      return;
    }
    try {
      setConnectionState((prev) => (prev === 'Connected' ? 'Reconnecting' : 'Connecting'));
      const { HubConnectionBuilder } = await import('@microsoft/signalr');
      const newConnection = new HubConnectionBuilder()
        .withUrl(buildHubUrl(resolveHubPath(hubName)), {
          accessTokenFactory: () => token,
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 20000])
        .withServerTimeout(30000)
        .build();

      newConnection.onreconnecting(() => setConnectionState('Reconnecting'));
      newConnection.onreconnected(() => {
        setConnectionState('Connected');
        reconnectAttempts.current = 0;
      });
      newConnection.onclose(() => {
        if (disposedRef.current) return;
        // Retry after automatic-reconnect exhausts, until the manual budget is
        // also spent — then move to the terminal FailedPermanently state.
        if (reconnectAttempts.current >= MAX_MANUAL_RECONNECT_ATTEMPTS) {
          setConnectionState('FailedPermanently');
          emitExhaustionMetric(resolveHubPath(hubName));
          return;
        }
        setConnectionState('Disconnected');
        const delay = getReconnectDelay(reconnectAttempts.current);
        reconnectAttempts.current += 1;
        if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = setTimeout(() => {
          if (!disposedRef.current) void connect();
        }, delay);
      });

      await newConnection.start();
      if (disposedRef.current) {
        void newConnection.stop().catch(() => undefined);
        return;
      }
      connRef.current = newConnection as unknown as SignalRConnection;
      setConnection(newConnection as unknown as SignalRConnection);
      setConnectionState('Connected');
      setError(null);
      reconnectAttempts.current = 0;
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      if (disposedRef.current) return;
      if (reconnectAttempts.current >= MAX_MANUAL_RECONNECT_ATTEMPTS) {
        setConnectionState('FailedPermanently');
        emitExhaustionMetric(resolveHubPath(hubName));
        return;
      }
      setConnectionState('Disconnected');
      const delay = getReconnectDelay(reconnectAttempts.current);
      reconnectAttempts.current += 1;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = setTimeout(() => {
        if (!disposedRef.current) void connect();
      }, delay);
    }
  }, [token, hubName]);

  useEffect(() => {
    disposedRef.current = false;
    // Each (re)mount or token change starts with a full reconnect budget, so a
    // hub that previously failed permanently gets a clean retry allowance once
    // a new token arrives (connect's identity changes with the token).
    reconnectAttempts.current = 0;
    void connect();
    return () => {
      disposedRef.current = true;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      const conn = connRef.current;
      connRef.current = null;
      if (conn) void conn.stop().catch(() => undefined);
      setConnection(null);
      setConnectionState('Disconnected');
    };
  }, [connect]);

  return { connection, connectionState, error };
};

const useSharedSignalR = (
  registry: HubRegistry,
  hubName: string,
): UseSignalRResult => {
  // Acquire on mount, release on unmount — ref-counted in the provider.
  useEffect(() => {
    registry.acquire(hubName);
    return () => registry.release(hubName);
  }, [registry, hubName]);

  const [, force] = useState(0);
  useEffect(() => {
    const unsub = registry.subscribe(hubName, () => force((n) => n + 1));
    // Pull an immediate snapshot in case state changed between acquire and
    // subscribe.
    force((n) => n + 1);
    return unsub;
  }, [registry, hubName]);

  return registry.snapshot(hubName);
};

/**
 * Hook to access a SignalR hub connection.
 *
 * When wrapped in a {@link HubConnectionProvider}, all callers for a given hub
 * share ONE ref-counted connection. Without the provider it falls back to a
 * per-hook connection (legacy behavior).
 *
 * @param hubName - Name of the SignalR hub (PascalCase "AlarmHub" or
 *                  lowercase "alarms" both work).
 *
 * @example
 * const { connection, connectionState, error } = useSignalR('EquipmentHub');
 */
export const useSignalR = (hubName: string): UseSignalRResult => {
  const registry = useContext(HubRegistryContext);
  // Both branches are hooks; the branch is stable for the component's lifetime
  // because the provider presence never flips mid-mount.
  if (registry) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useSharedSignalR(registry, hubName);
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useStandaloneSignalR(hubName);
};

/**
 * Hook to join a SignalR group on mount and leave on unmount
 *
 * @example
 * useSignalRGroup('EquipmentHub', 'line-1-equipment');
 */
export const useSignalRGroup = (hubName: string, groupName: string): void => {
  const { connection, connectionState } = useSignalR(hubName);

  useEffect(() => {
    if (connection && connectionState === 'Connected') {
      connection
        .invoke('JoinGroup', groupName)
        .catch((err) => console.error('Failed to join group:', err));

      return () => {
        connection
          .invoke('LeaveGroup', groupName)
          .catch((err) => console.error('Failed to leave group:', err));
      };
    }
    return undefined;
  }, [connection, connectionState, groupName]);
};

/**
 * Reconciliation options for {@link useSignalREvent} (A5-025).
 *
 * When `reconcile` is supplied, the pushed payload is MERGED into the named
 * cache entry via {@link applyLiveEvent} before any invalidation, stamping the
 * push with the receive time and biasing it to win timestamp ties. This means a
 * slow REST refetch (or a late mutation response) landing afterward cannot roll
 * the UI back to a value older than the push — the documented merge strategy.
 */
export interface SignalREventReconcile<T> {
  /** Cache key to merge the pushed payload into (use `apiQueryKey(...)`). */
  queryKey: QueryKey;
  /**
   * Optional projection from the event payload to the cached value's shape.
   * Defaults to identity (the event payload IS the cached value).
   */
  select?: (data: T) => unknown;
}

/**
 * Hook to subscribe to a specific SignalR event with optional cache
 * reconciliation + invalidation.
 *
 * The caller's `handler` is held in a ref so the .on/.off subscription effect
 * does not depend on the handler's identity — it would otherwise tear down and
 * rebuild the subscription on every render.
 *
 * Ordering per event: (1) caller `handler`, (2) `reconcile` merge (push wins
 * ties), (3) `invalidateQueries`. Step 2 ensures the cache holds the fresh
 * pushed value BEFORE step 3 schedules a refetch, so an out-of-order slow REST
 * response reconciles against the newer marker instead of clobbering it.
 *
 * @example
 * useSignalREvent<EquipmentData>(
 *   'EquipmentHub',
 *   'EquipmentUpdated',
 *   (data) => setLocal(data),
 *   { reconcile: { queryKey: apiQueryKey('/equipment/' + id, tenantId) } }
 * );
 */
export const useSignalREvent = <T,>(
  hubName: string,
  eventName: string,
  handler: (data: T) => void,
  options?: {
    invalidateQueries?: QueryKey[];
    reconcile?: SignalREventReconcile<T>;
  },
): void => {
  const { connection, connectionState } = useSignalR(hubName);
  const queryClient = useQueryClient();

  // Hold the latest handler + options in refs so the subscription effect only
  // depends on the connection identity, event name, and connection state.
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (connection && connectionState === 'Connected') {
      const wrappedHandler = (...args: unknown[]) => {
        const data = args[0] as T;
        handlerRef.current(data);

        // (2) Merge the pushed payload into the cache with the push-biased
        // reconciliation rule, so a subsequent slow REST refetch cannot roll
        // the value back below this push's freshness marker.
        const reconcile = optionsRef.current?.reconcile;
        if (reconcile && queryClient) {
          const value = reconcile.select ? reconcile.select(data) : data;
          applyLiveEvent(queryClient, reconcile.queryKey, value);
        }

        const invalidate = optionsRef.current?.invalidateQueries;
        if (invalidate && queryClient) {
          invalidate.forEach((queryKey) => {
            queryClient.invalidateQueries({ queryKey });
          });
        }
      };

      connection.on(eventName, wrappedHandler);
      return () => {
        connection.off(eventName, wrappedHandler);
      };
    }
    return undefined;
  }, [connection, connectionState, eventName, queryClient]);
};
